import React from 'react';
import { ArrowRightIcon, CheckIcon, BookOpenIcon, MicIcon, CalendarIcon, LayersIcon } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function LeadingInComplexity() {
  const {
    t
  } = useLanguage();
  const signs = t('leadingInComplexity.signs') as string[];
  const whatWeDoItems = t('leadingInComplexity.whatWeDoItems') as string[];
  return <PageTransition data-id="element-1473">
      <div className="pt-12 pb-24" data-id="element-1474">
        {/* Hero */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1475">
          <DecorativeShape type="blob" color="bg-blue-100/60" className="top-0 right-0 w-[500px] h-[500px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-1476" />
          <Animate className="max-w-3xl" data-id="element-1477">
            <span className="inline-block px-4 py-2 bg-blue-50 text-blue-700 font-medium rounded-full text-sm mb-6" data-id="element-1478">
              {t('leadingInComplexity.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1479">
              {t('leadingInComplexity.title')}
              <br data-id="element-1480" />
              <span className="text-blue-600" data-id="element-1481">{t('leadingInComplexity.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-6 leading-relaxed" data-id="element-1482">
              {t('leadingInComplexity.intro1')}
            </p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1483">
              {t('leadingInComplexity.intro2')}
            </p>
            <Link to="/contact" data-id="element-1484">
              <Button size="lg" rightIcon={<ArrowRightIcon size={20} data-id="element-1486" />} data-id="element-1485">
                {t('leadingInComplexity.heroCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        {/* Signs */}
        <section className="bg-cream py-20 mb-20" data-id="element-1487">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1488">
            <Animate className="mb-12" data-id="element-1489">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-1490">
                {t('leadingInComplexity.signsTitle')}
              </h2>
            </Animate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1491">
              {signs.map((sign, i) => <Animate key={i} delay={i * 0.05} data-id="element-1492">
                  <div className="flex items-start gap-3 bg-white rounded-2xl p-5 shadow-sm" data-id="element-1493">
                    <div className="mt-0.5 p-1 rounded-full bg-blue-50 text-blue-600 flex-shrink-0" data-id="element-1494">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1495" />
                    </div>
                    <span className="text-gray-700" data-id="element-1496">{sign}</span>
                  </div>
                </Animate>)}
            </div>
          </div>
        </section>

        {/* What we work on */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1497">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-1498">
            <Animate data-id="element-1499">
              <h2 className="text-3xl font-bold text-charcoal mb-6" data-id="element-1500">
                {t('leadingInComplexity.whatWeDoTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1501">
                {t('leadingInComplexity.whatWeDoP1')}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1502">
                {t('leadingInComplexity.whatWeDoP2')}
              </p>
            </Animate>
            <Animate delay={0.1} data-id="element-1503">
              <div className="space-y-3" data-id="element-1504">
                {whatWeDoItems.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-1505">
                    <div className="mt-1 p-1 rounded-full bg-blue-50 text-blue-600 flex-shrink-0" data-id="element-1506">
                      <CheckIcon size={14} strokeWidth={3} data-id="element-1507" />
                    </div>
                    <span className="text-gray-700" data-id="element-1508">{item}</span>
                  </div>)}
              </div>
            </Animate>
          </div>
        </section>

        {/* Ways to work together */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1509">
          <Animate className="mb-12" data-id="element-1510">
            <h2 className="text-3xl font-bold text-charcoal" data-id="element-1511">
              {t('leadingInComplexity.waysTitle')}
            </h2>
            <p className="text-gray-500 mt-2" data-id="element-1512">{t('leadingInComplexity.waysSubtitle')}</p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1513">
            {[{
            icon: <BookOpenIcon size={24} className="text-blue-600" data-id="element-1514" />,
            typeBg: 'bg-blue-50 text-blue-700',
            typeKey: 'courseLabel',
            titleKey: 'course1Title',
            descKey: 'course1Desc',
            path: '/services/courses'
          }, {
            icon: <LayersIcon size={24} className="text-teal" data-id="element-1515" />,
            typeBg: 'bg-teal/10 text-teal',
            typeKey: 'workshopLabel',
            titleKey: 'workshop1Title',
            descKey: 'workshop1Desc',
            path: '/services/workshops'
          }, {
            icon: <MicIcon size={24} className="text-yellow-600" data-id="element-1516" />,
            typeBg: 'bg-yellow-100 text-yellow-700',
            typeKey: 'talkLabel',
            titleKey: 'talk1Title',
            descKey: 'talk1Desc',
            path: '/services/talks'
          }].map((item, i) => <Animate key={i} delay={i * 0.1} data-id="element-1517">
                <Card hoverEffect className="h-full p-6 flex flex-col" data-id="element-1518">
                  <div className="flex items-center gap-3 mb-4" data-id="element-1519">
                    {item.icon}
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${item.typeBg}`} data-id="element-1520">
                      {t(`leadingInComplexity.${item.typeKey}`)}
                    </span>
                  </div>
                  <h3 className="font-bold text-lg text-charcoal mb-2" data-id="element-1521">
                    {t(`leadingInComplexity.${item.titleKey}`)}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-4" data-id="element-1522">
                    {t(`leadingInComplexity.${item.descKey}`)}
                  </p>
                  <Link to={item.path} data-id="element-1523">
                    <Button variant="outline" size="sm" rightIcon={<ArrowRightIcon size={16} data-id="element-1525" />} data-id="element-1524">
                      {t('leadingInComplexity.learnMore')}
                    </Button>
                  </Link>
                </Card>
              </Animate>)}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-1526">
          <Animate data-id="element-1527">
            <Card className="p-12 bg-gradient-to-br from-blue-50 to-indigo-50 border-0" data-id="element-1528">
              <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-md" data-id="element-1529">
                <CalendarIcon size={32} className="text-blue-600" data-id="element-1530" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-1531">{t('leadingInComplexity.ctaTitle')}</h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-1532">{t('leadingInComplexity.ctaDesc')}</p>
              <Link to="/contact" data-id="element-1533">
                <Button variant="primary" rightIcon={<ArrowRightIcon size={18} data-id="element-1535" />} data-id="element-1534">
                  {t('leadingInComplexity.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}